import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook, MapPin, MessageCircle, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n";
import { FACEBOOK, MAPS, PHONE, PHONE_LABEL, WHATSAPP } from "@/lib/site";
import { Logo, Reveal, SectionHead } from "./Primitives";

export const Faq = () => {
  const { t } = useLang();
  return (
    <section data-testid="faq-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4"><SectionHead eyebrow={t.faq.eyebrow} title={t.faq.title} testid="faq" /></div>
      <Accordion type="single" collapsible className="lg:col-span-8">
        {t.faq.items.map((f, i) => (
          <AccordionItem key={i} value={`q${i}`} className="border-[color:var(--line)]">
            <AccordionTrigger data-testid={`faq-q-${i}`} className="display text-xl sm:text-2xl text-left py-6 hover:no-underline hover:text-[color:var(--saffron-soft)]">{f.q}</AccordionTrigger>
            <AccordionContent data-testid={`faq-a-${i}`} className="text-base text-[color:var(--muted)] leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};

const CtaBand = ({ t }) => (
  <Reveal className="card relative overflow-hidden p-8 sm:p-14 lg:p-20">
    <div className="ember absolute -right-32 -top-32 w-[520px] h-[520px]" aria-hidden="true" />
    <div className="relative grid lg:grid-cols-12 gap-10 items-end">
      <h2 data-testid="cta-title" className="lg:col-span-7 display text-4xl sm:text-6xl leading-[1.02]">{t.cta.title}</h2>
      <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
        <Link data-testid="cta-book-btn" to="/book" className="pill-solid px-7 py-3.5 text-sm">{t.cta.book}<ArrowUpRight className="w-4 h-4 ml-2" /></Link>
        <a data-testid="cta-whatsapp-btn" href={WHATSAPP} target="_blank" rel="noreferrer" className="pill-ghost px-6 py-3.5 text-sm"><MessageCircle className="w-4 h-4 mr-2" />{t.cta.whatsapp}</a>
        <a data-testid="cta-phone-btn" href={`tel:${PHONE}`} className="pill-ghost px-6 py-3.5 text-sm"><Phone className="w-4 h-4 mr-2" />{PHONE_LABEL}</a>
      </div>
    </div>
  </Reveal>
);

const FacebookBand = ({ t }) => (
  <Reveal className="card mt-5 p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
    <div className="flex items-start gap-5">
      <Facebook className="w-10 h-10 shrink-0 text-[color:var(--gold)]" strokeWidth={1.2} />
      <div>
        <h3 className="display text-3xl">{t.cta.facebookTitle}</h3>
        <p className="mt-1 text-sm text-[color:var(--muted)]">{t.cta.facebookText}</p>
      </div>
    </div>
    <a data-testid="facebook-group-btn" href={FACEBOOK} target="_blank" rel="noreferrer" className="pill-solid px-7 py-3.5 text-sm self-start md:self-auto">{t.cta.facebook}<ArrowUpRight className="w-4 h-4 ml-2" /></a>
  </Reveal>
);

export const Contact = () => {
  const { t } = useLang();
  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 pb-16">
      <CtaBand t={t} />
      <FacebookBand t={t} />
      <div className="mt-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6"><SectionHead eyebrow={t.chamber.eyebrow} title={t.chamber.title} testid="chamber" /></div>
        <Reveal className="lg:col-span-6 lg:pt-12">
          <p data-testid="chamber-address" className="flex gap-3 text-lg text-[color:var(--ink)]"><MapPin className="w-5 h-5 mt-1 text-[color:var(--saffron)] shrink-0" />{t.chamber.address}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a data-testid="chamber-directions-btn" href={MAPS} target="_blank" rel="noreferrer" className="pill-ghost px-6 py-3 text-sm">{t.chamber.directions}<ArrowUpRight className="w-4 h-4 ml-2" /></a>
            <a data-testid="chamber-phone-btn" href={`tel:${PHONE}`} className="pill-ghost px-6 py-3 text-sm"><Phone className="w-4 h-4 mr-2" />{PHONE_LABEL}</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="border-t border-[color:var(--line)]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-10 flex flex-col md:flex-row gap-6 md:items-center justify-between text-sm text-[color:var(--muted)]">
        <div className="flex items-center gap-4"><img src="/logo.png" alt="Vedic Yagya Astrology" className="w-20 h-auto" /><span>© {new Date().getFullYear()} Vedic Yagya Astrology · Pandit Ajay Sagar Shukla. {t.footer.rights}</span></div>
        <div className="flex gap-6">
          <a data-testid="footer-call" href={`tel:${PHONE}`} className="nav-link hover:text-[color:var(--ink)]">{t.footer.call}</a>
          <a data-testid="footer-whatsapp" href={WHATSAPP} target="_blank" rel="noreferrer" className="nav-link hover:text-[color:var(--ink)]">{t.footer.whatsapp}</a>
          <a data-testid="footer-facebook" href={FACEBOOK} target="_blank" rel="noreferrer" className="nav-link hover:text-[color:var(--ink)]">Facebook</a>
          <Link data-testid="footer-book" to="/book" className="nav-link hover:text-[color:var(--ink)]">{t.footer.book}</Link>
          <Link data-testid="footer-admin" to="/admin" className="nav-link hover:text-[color:var(--ink)]">Admin</Link>
        </div>
      </div>
    </footer>
  );
};
